import { useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import Section from './SettingsPageSection';
import { LabeledCheckbox } from '../../generic/Forms';
import Link from '../../generic/Link';
import type { NotificationsProjectSettings } from '../../../../shared/settings/project/notifications';
import type { MinimalUser } from '../../../../shared/user';
import type { Notification } from '../../../../shared/model-typings';
import { withTargetChecked } from '../../../utils';
import SelectUsersButton from '../../settings/project/notifications/SelectUsersButton';
import ModalEditNotifications from '../../modals/ModalEditNotifications';

type ProjectNotificationsSettingsProps = Omit<NotificationsProjectSettings, 'notificationDefaultEmailScanUserType'> & {
  //  This is actually optional here.
  notificationDefaultEmailScanUserType?: NotificationsProjectSettings['notificationDefaultEmailScanUserType'];
  onUpdateNotificationDefaultEnabled: (notificationDefaultEnabled: boolean) => void;
  isEmailNotificationsEnabled: boolean;
  onIsEmailNotificationsEnabledChange: (isEmailNotificationsEnabled: boolean) => void;
  onSelectUsersDropdownChange: (userType: NotificationsProjectSettings['notificationDefaultEmailScanUserType']) => void;
  onToggleSlackNotifications: (notificationDefaultSlackScan: boolean) => void;
  users: MinimalUser[];
  onConfirmCustomUserSelection: (subscribedUserIds: number[]) => void;
};

/**
 * ProjectNotificationsSettings is the generic presentational component for project notification settings.
 * This page is the exact same for both the project and org settings pages, which is why it lives here.
 */
const ProjectNotificationsSettings: VoidFunctionComponent<ProjectNotificationsSettingsProps> = ({
  notificationDefaultEnabled,
  onUpdateNotificationDefaultEnabled,
  isEmailNotificationsEnabled,
  onIsEmailNotificationsEnabledChange,
  notificationDefaultEmailScanUserType,
  onSelectUsersDropdownChange,
  notificationDefaultSlackScan,
  onToggleSlackNotifications,
  users,
  notificationDefaultEmailScanUsers,
  onConfirmCustomUserSelection,
}) => {
  const [shouldDisplayUsersModal, setShouldDisplayUsersModal] = useState<boolean>(false);
  //  TODO: This is how the existing ModalEditNotifications handles this. Later we should refactor
  //  the ModalEditNotifications so that it just takes a list of selected userIds and the update function.
  const editingEmailNotification = useMemo(() => ({
    channel: 'SCAN',
    service: 'EMAIL',
    subscribed_users: notificationDefaultEmailScanUsers || [],
  } as Notification), [notificationDefaultEmailScanUsers]);
  return (
    <>
      <LabeledCheckbox
        label="Enable notifications"
        onChange={withTargetChecked(onUpdateNotificationDefaultEnabled)}
        isChecked={notificationDefaultEnabled}
      />
      <Section.Paragraph>
        Enabling this will use the following notification settings for all new projects
      </Section.Paragraph>
      <Section.Indented isDisabled={!notificationDefaultEnabled}>
        <LabeledCheckbox
          label="Enable email notifications"
          isChecked={isEmailNotificationsEnabled}
          isDisabled={!notificationDefaultEnabled}
          onChange={withTargetChecked(onIsEmailNotificationsEnabledChange)}
        />
        {notificationDefaultEmailScanUserType && notificationDefaultEnabled && (
          <SelectUsersButton
            isDisabled={!notificationDefaultEnabled}
            userType={notificationDefaultEmailScanUserType}
            onSelect={userType => {
              onSelectUsersDropdownChange(userType);
              //  Open the modal if they are selecting a custom subset of users
              if (userType === 'custom') {
                setShouldDisplayUsersModal(true);
              }
            }}
          />
        )}
        <Section.Paragraph>Enabling this will turn on email notifications</Section.Paragraph>
        <LabeledCheckbox
          label="Enable slack notifications"
          isChecked={notificationDefaultSlackScan}
          isDisabled={!notificationDefaultEnabled}
          onChange={withTargetChecked(onToggleSlackNotifications)}
        />
        <Section.Paragraph>
          NOTE: To manage Slack go to the
          {' '}
          <Link to="/account/settings/integrations/slack" variant="primary" isDisabled={!notificationDefaultEnabled}>
            Slack Integration Settings
          </Link>
        </Section.Paragraph>
      </Section.Indented>
      {shouldDisplayUsersModal && (
        <ModalEditNotifications
          editingNotification={editingEmailNotification}
          users={users || []}
          onConfirm={onConfirmCustomUserSelection}
          onHide={() => setShouldDisplayUsersModal(false)}
        />
      )}
    </>
  );
};

export default ProjectNotificationsSettings;
